import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import importService from "../../services/importService";
import LoadingSpinner from "../common/LoadingSpinner";
import { formatDate } from "../../utils/formatters";

const ImportHistory = ({ refreshTrigger }) => {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const loadImports = useCallback(async () => {
    try {
      setLoading(true);
      const response = await importService.getImports({
        page: pagination.page,
        limit: pagination.limit,
      });

      setImports(response.data || []);
      setPagination(
        response.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          pages: 0,
        }
      );
    } catch (err) {
      setError("Erreur lors du chargement de l'historique");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    loadImports();
  }, [loadImports]);

  // Effet pour rafraîchir l'historique quand une importation se termine
  useEffect(() => {
    if (refreshTrigger) {
      loadImports();
    }
  }, [refreshTrigger, loadImports]);

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      "in-progress": "bg-blue-100 text-blue-800",
      success: "bg-green-100 text-green-800",
      completed: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
    };

    const statusLabels = {
      pending: "En attente",
      processing: "En cours",
      "in-progress": "En cours",
      success: "Succès",
      completed: "Terminé",
      failed: "Échec",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          statusClasses[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {statusLabels[status] || status}
      </span>
    );
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  if (loading) {
    return (
      <div className="p-6">
        <LoadingSpinner message="Chargement de l'historique..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (imports.length === 0) {
    return (
      <div className="p-6 text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          stroke="currentColor"
          fill="none"
          viewBox="0 0 48 48"
        >
          <path
            d="M34 40h10v-4a6 6 0 00-10.712-3.714M34 40H14m20 0v-4a9.971 9.971 0 00-.712-3.714M14 40H4v-4a6 6 0 0110.713-3.714M14 40v-4c0-1.313.253-2.566.713-3.714m0 0A9.971 9.971 0 0118 28c2.624 0 4.928 1.018 6.713 2.714"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          Aucune importation
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Aucune importation n'a encore été effectuée.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Version desktop avec table responsive */}
      <div className="hidden lg:block">
        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-xs">
                  Fichier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Résumé
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Importé par
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {imports.map((importItem) => (
                <tr key={importItem._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-col">
                      <span>{formatDate(importItem.startTime)}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(importItem.startTime).toLocaleTimeString(
                          "fr-FR",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                    <div className="truncate" title={importItem.filename}>
                      {importItem.filename}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(importItem.status)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="space-y-1">
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                          {importItem.newRecords || 0} nouveaux
                        </span>
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {importItem.updatedRecords || 0} modifiés
                        </span>
                        {importItem.errorCount > 0 && (
                          <span className="bg-red-100 text-red-800 px-2 py-1 rounded">
                            {importItem.errorCount} erreurs
                          </span>
                        )}
                      </div>
                      {importItem.changesSummary &&
                        importItem.changesSummary.length > 0 && (
                          <div className="text-xs text-blue-600">
                            <span className="font-medium">Modifications: </span>
                            {importItem.changesSummary.length} contrats modifiés
                          </div>
                        )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-700">
                            {(importItem.importedBy?.username || "N/A")
                              .charAt(0)
                              .toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-2">
                        <div className="text-sm text-gray-900 truncate max-w-20">
                          {importItem.importedBy?.username || "N/A"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <Link
                      to={`/imports/${importItem._id}`}
                      className="inline-flex items-center text-primary-600 hover:text-primary-900 transition-colors duration-200"
                    >
                      Détails
                      <svg
                        className="ml-1 h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Version tablette */}
      <div className="hidden md:block lg:hidden">
        <div className="space-y-4 p-4">
          {imports.map((importItem) => (
            <div
              key={importItem._id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0 mr-4">
                  <h3
                    className="text-sm font-medium text-gray-900 truncate"
                    title={importItem.filename}
                  >
                    {importItem.filename}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDate(importItem.startTime)} à{" "}
                    {new Date(importItem.startTime).toLocaleTimeString(
                      "fr-FR",
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    )}
                  </p>
                </div>
                {getStatusBadge(importItem.status)}
              </div>

              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="text-center bg-green-50 rounded p-2">
                  <div className="text-lg font-semibold text-green-800">
                    {importItem.newRecords || 0}
                  </div>
                  <div className="text-xs text-green-600">Nouveaux</div>
                </div>
                <div className="text-center bg-blue-50 rounded p-2">
                  <div className="text-lg font-semibold text-blue-800">
                    {importItem.updatedRecords || 0}
                  </div>
                  <div className="text-xs text-blue-600">Modifiés</div>
                </div>
                <div className="text-center bg-red-50 rounded p-2">
                  <div className="text-lg font-semibold text-red-800">
                    {importItem.errorCount || 0}
                  </div>
                  <div className="text-xs text-red-600">Erreurs</div>
                </div>
              </div>

              {importItem.changesSummary &&
                importItem.changesSummary.length > 0 && (
                  <div className="mb-3 p-2 bg-blue-50 rounded text-xs">
                    <span className="font-medium text-blue-800">
                      Modifications détectées:{" "}
                    </span>
                    <span className="text-blue-600">
                      {importItem.changesSummary.length} contrats modifiés
                    </span>
                  </div>
                )}

              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Par {importItem.importedBy?.username || "N/A"}
                </div>
                <Link
                  to={`/imports/${importItem._id}`}
                  className="inline-flex items-center text-sm text-primary-600 hover:text-primary-900 font-medium"
                >
                  Voir détails
                  <svg
                    className="ml-1 h-3 w-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Version mobile */}
      <div className="md:hidden">
        <div className="space-y-3 p-4">
          {imports.map((importItem) => (
            <div
              key={importItem._id}
              className="bg-white border border-gray-200 rounded-lg p-4"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0 mr-2">
                  <p
                    className="text-sm font-medium text-gray-900 truncate"
                    title={importItem.filename}
                  >
                    {importItem.filename}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDate(importItem.startTime)}
                  </p>
                </div>
                {getStatusBadge(importItem.status)}
              </div>

              <div className="grid grid-cols-3 gap-1 mb-3 text-center">
                <div className="bg-green-50 rounded p-1">
                  <div className="text-sm font-semibold text-green-800">
                    {importItem.newRecords || 0}
                  </div>
                  <div className="text-xs text-green-600">Nouveaux</div>
                </div>
                <div className="bg-blue-50 rounded p-1">
                  <div className="text-sm font-semibold text-blue-800">
                    {importItem.updatedRecords || 0}
                  </div>
                  <div className="text-xs text-blue-600">Modifiés</div>
                </div>
                <div className="bg-red-50 rounded p-1">
                  <div className="text-sm font-semibold text-red-800">
                    {importItem.errorCount || 0}
                  </div>
                  <div className="text-xs text-red-600">Erreurs</div>
                </div>
              </div>

              {importItem.changesSummary &&
                importItem.changesSummary.length > 0 && (
                  <div className="mb-2 p-2 bg-blue-50 rounded text-xs">
                    <span className="font-medium text-blue-800">
                      Modifications:{" "}
                    </span>
                    <span className="text-blue-600">
                      {importItem.changesSummary.length} contrats
                    </span>
                  </div>
                )}

              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">
                  Par {importItem.importedBy?.username || "N/A"}
                </span>
                <Link
                  to={`/imports/${importItem._id}`}
                  className="inline-flex items-center text-primary-600 hover:text-primary-900 font-medium"
                >
                  Détails
                  <svg
                    className="ml-1 h-3 w-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="bg-white border-t border-gray-200 px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between">
            {/* Mobile pagination */}
            <div className="flex-1 flex justify-between items-center sm:hidden">
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Première page"
                >
                  ⟪
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Préc.
                </button>
              </div>

              <span className="text-sm text-gray-700 flex items-center px-2">
                Page {pagination.page} sur {pagination.pages}
              </span>

              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suiv.
                </button>
                <button
                  onClick={() => handlePageChange(pagination.pages)}
                  disabled={pagination.page === pagination.pages}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Dernière page"
                >
                  ⟫
                </button>
              </div>
            </div>

            {/* Desktop pagination */}
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Affichage de{" "}
                  <span className="font-medium">
                    {(pagination.page - 1) * pagination.limit + 1}
                  </span>{" "}
                  à{" "}
                  <span className="font-medium">
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total
                    )}
                  </span>{" "}
                  sur <span className="font-medium">{pagination.total}</span>{" "}
                  résultats
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {/* Bouton Première page */}
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Première page"
                  >
                    <span className="sr-only">Première page</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  {/* Bouton Précédent */}
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Page précédente"
                  >
                    <span className="sr-only">Précédent</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {/* Pages numérotées */}
                  {[...Array(Math.min(5, pagination.pages))].map((_, index) => {
                    let pageNum;
                    if (pagination.pages <= 5) {
                      pageNum = index + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = index + 1;
                    } else if (pagination.page >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + index;
                    } else {
                      pageNum = pagination.page - 2 + index;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pagination.page === pageNum
                            ? "z-10 bg-primary-50 border-primary-500 text-primary-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                        title={`Page ${pageNum}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  {/* Points de suspension si nécessaire */}
                  {pagination.pages > 5 &&
                    pagination.page < pagination.pages - 2 && (
                      <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        ...
                      </span>
                    )}

                  {/* Bouton Suivant */}
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Page suivante"
                  >
                    <span className="sr-only">Suivant</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {/* Bouton Dernière page */}
                  <button
                    onClick={() => handlePageChange(pagination.pages)}
                    disabled={pagination.page === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Dernière page"
                  >
                    <span className="sr-only">Dernière page</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 5l7 7-7 7M5 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}{" "}
    </div>
  );
};

export default ImportHistory;
